import React, { useEffect, useState } from 'react';
import { useApi, configApiRef, fetchApiRef } from '@backstage/core-plugin-api';
import type { FieldExtensionComponentProps } from '@backstage/plugin-scaffolder-react';
import FormControl from '@material-ui/core/FormControl';
import InputLabel from '@material-ui/core/InputLabel';
import Select from '@material-ui/core/Select';
import MenuItem from '@material-ui/core/MenuItem';
import FormHelperText from '@material-ui/core/FormHelperText';

interface Subscription {
  id: string;
  name: string;
  state: string;
}

/**
 * Custom scaffolder field that lists Azure subscriptions by name.
 * The selected value is the subscription ID (UUID) — the display label is
 * the human-readable subscription name.
 *
 * Usage in template.yaml:
 * ```yaml
 * azureSubscriptionId:
 *   title: Azure Subscription
 *   type: string
 *   description: Azure subscription where resources will be deployed
 *   ui:field: AzureSubscriptionPicker
 * ```
 */
export const AzureSubscriptionPicker = (
  props: FieldExtensionComponentProps<string>,
) => {
  const { onChange, rawErrors, required, formData, uiSchema, schema } = props;
  const config = useApi(configApiRef);
  const fetchApi = useApi(fetchApiRef);

  const [subscriptions, setSubscriptions] = useState<Subscription[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | undefined>();

  useEffect(() => {
    (async () => {
      try {
        const backendUrl = config.getString('backend.baseUrl');
        const res = await fetchApi.fetch(`${backendUrl}/api/azure-apps/subscriptions`);
        if (!res.ok) {
          throw new Error(`HTTP ${res.status}`);
        }
        const data: Subscription[] = await res.json();
        // Only show enabled subscriptions
        const enabled = data.filter(s => s.state === 'Enabled');
        setSubscriptions(enabled);

        // Auto-select if there's only one subscription
        if (enabled.length === 1 && !formData) {
          onChange(enabled[0].id);
        }
      } catch (err: any) {
        setError(err.message ?? 'Failed to load subscriptions');
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const label = (schema?.title as string) ?? 'Azure Subscription';
  const description =
    (schema?.description as string) ?? uiSchema?.['ui:description'] ?? '';
  const hasError = rawErrors && rawErrors.length > 0;

  if (loading) {
    return (
      <FormControl fullWidth margin="normal">
        <InputLabel>{label}</InputLabel>
        <Select disabled value="">
          <MenuItem value="">Loading subscriptions...</MenuItem>
        </Select>
        {description && <FormHelperText>{description}</FormHelperText>}
      </FormControl>
    );
  }

  if (error) {
    return (
      <FormControl fullWidth margin="normal" error>
        <InputLabel>{label}</InputLabel>
        <Select disabled value="">
          <MenuItem value="">Failed to load subscriptions</MenuItem>
        </Select>
        <FormHelperText>{error}</FormHelperText>
      </FormControl>
    );
  }

  if (subscriptions.length === 0) {
    return (
      <FormControl fullWidth margin="normal" error>
        <InputLabel>{label}</InputLabel>
        <Select disabled value="">
          <MenuItem value="">No subscriptions found</MenuItem>
        </Select>
        <FormHelperText>
          No enabled Azure subscriptions were found. Check your service
          principal permissions.
        </FormHelperText>
      </FormControl>
    );
  }

  return (
    <FormControl
      fullWidth
      margin="normal"
      required={required}
      error={hasError}
    >
      <InputLabel>{label}</InputLabel>
      <Select
        value={formData ?? ''}
        onChange={e => onChange(e.target.value as string)}
      >
        {subscriptions.map(s => (
          <MenuItem key={s.id} value={s.id}>
            {s.name} ({s.id})
          </MenuItem>
        ))}
      </Select>
      {description && <FormHelperText>{description}</FormHelperText>}
    </FormControl>
  );
};
